import { Mol } from '@moleculeone/types';
import React, { useEffect, useState } from 'react';

import { styled } from '../../../stitches.config';
import { CommonAtoms, CommonFragments, EditorSpacer, HotKeys, ModeToolbar, Toolbar } from '../BaseEditor/components';
import { useCompoundEditor } from '../BaseEditor/hooks/useCompoundEditor';
import { useKeyboard } from '../BaseEditor/hooks/useKeyboard';
import { molToSmiles } from '../BaseEditor/utils/conversion';
import { CompoundEditorCompound } from './components/CompoundEditorCompound';
import { useSmilesToMol } from '../../../hooks/useSmilesToMol';

const EditorContainer = styled('div', {
  display: 'flex',
  paddingTop: '$5',
});

const DrawingSpacesContainer = styled('div', {
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  zIndex: 1,
  margin: '0 $4',
  variants: {
    minimal: {
      true: {
        height: '10rem',
      },
      false: {
        height: '30rem',
      },
    },
  },
});

const TopContext = styled('div', {
  'position': 'relative',
  'zIndex': 2,
  'display': 'flex',
  'justifyContent': 'space-between',
  'alignItems': 'flex-end',
  'opacity': 0,
  'transform': 'translateY(-0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const LeftContainer = styled('div', {
  'position': 'relative',
  'zIndex': 2,
  'opacity': 0,
  'transform': 'translateX(-0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const RightContainer = styled('div', {
  'display': 'flex',
  'flexDirection': 'column',
  'justifyContent': 'space-between',
  'alignItems': 'flex-end',
  'opacity': 0,
  'transform': 'translateX(0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&[hidden]': {
    opacity: 0,
    height: 0,
    pointerEvents: 'none',
  },
});
const RightToolbar = styled('div', {
  display: 'flex',
  gap: '$3',
  alignItems: 'flex-start',
});

type CompoundEditorProps = {
  loading: boolean;
  initialMol?: Mol;
  smiles: string;
  topContext?: React.ReactNode;
  arrowContext?: React.ReactNode;
  compoundChoices?: undefined | string[];
  minimal: boolean;
  onChange: (data: { mol: Mol; smiles: string }) => void;
  compoundContext?: React.ReactNode;
};
export const CompoundEditor = function ({
  loading,
  initialMol,
  smiles,
  topContext,
  minimal,
  onChange,
  compoundContext,
}: CompoundEditorProps) {
  const { spaces, lastMolsUpdate, actions, mode } = useCompoundEditor();
  const compoundSpace = spaces[0];
  const isSpaceEmpty = Object.keys(compoundSpace.mol.atoms).length === 0;

  const [areMolsInitialized, setMolsInitialized] = useState(false);

  const smilesToMolEnabled = !loading && !initialMol && smiles !== '';

  const { mols, loading: molsLoading } = useSmilesToMol({
    smiles: [smiles],
    enabled: smilesToMolEnabled,
  });

  useEffect(() => {
    if (!loading && initialMol) {
      actions.setMols([initialMol], true);
    }
  }, [loading]);

  useEffect(() => {
    if (smilesToMolEnabled && !molsLoading && isSpaceEmpty) {
      actions.setMols(mols);
    }
  }, [molsLoading, mols, isSpaceEmpty, smilesToMolEnabled]);

  useEffect(() => {
    async function emitChange() {
      const compoundMol = compoundSpace.mol;
      const smiles = await molToSmiles(compoundMol);

      onChange({
        mol: compoundMol,
        smiles,
      });
    }

    if (areMolsInitialized) {
      emitChange();
    }

    setMolsInitialized(true);
  }, [lastMolsUpdate]);

  useKeyboard({
    onKeyDown: (event: KeyboardEvent) => {
      actions.onKeyDown(event);
    },
    onKeyUp: (event: KeyboardEvent) => {
      if (event.key !== '?') {
        actions.onKeyUp(event);
      }
    },
  });

  return (
    <>
      <EditorSpacer hidden={minimal}>
        <TopContext data-visible={!loading}>
          <Toolbar actions={actions} smiles={smiles} />
          {topContext}
        </TopContext>
      </EditorSpacer>

      <EditorContainer>
        <LeftContainer data-visible={!loading}>
          <ModeToolbar hidden={minimal} />
        </LeftContainer>

        <DrawingSpacesContainer minimal={minimal}>
          <CompoundEditorCompound
            loading={loading}
            spaceIdx={0}
            compound={compoundSpace}
            minimal={minimal}
            actions={actions}
            smiles={smiles}
            context={compoundContext}
          />
        </DrawingSpacesContainer>

        <RightContainer hidden={minimal} data-visible={!loading}>
          <RightToolbar>
            <CommonAtoms
              mode={mode}
              onPickAtom={mol => {
                actions.onFragmentAtomMode(mol, mol.atoms[0].idx);
              }}
            />

            <CommonFragments
              mode={mode}
              onPickAtom={(mol, idx) => {
                actions.onFragmentAtomMode(mol, idx);
              }}
              onPickBond={(mol, idx) => {
                actions.onFragmentBondMode(mol, idx);
              }}
            />
          </RightToolbar>

          <HotKeys />
        </RightContainer>
      </EditorContainer>
    </>
  );
};
